import React, { useState, useEffect } from 'react';
import Sidebar from '../Includes/Sidebar';
import Footer from '../Includes/Footer';
import { toast } from 'react-toastify';

export default function TokenCalling() {
    const [currentToken, setCurrentToken] = useState('A001');
    const [calledTokens, setCalledTokens] = useState([]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [displayToken, setDisplayToken] = useState('');
    const [tokenHistory, setTokenHistory] = useState([]);
    const [voicesLoaded, setVoicesLoaded] = useState(false);
    const [bellAudio, setBellAudio] = useState(null);
    const [isClearing, setIsClearing] = useState(false);
    const [shouldStop, setShouldStop] = useState(false); // New state to control stopping

    // API Configuration
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.azmrehabcentre.pk';
    const ADMIN_ID = localStorage.getItem('admin_id') || 1;
    const SESSION_ID = localStorage.getItem('session_id') || generateSessionId();

    // Generate unique session ID
    function generateSessionId() {
        const id = Date.now().toString(36) + Math.random().toString(36).substr(2);
        localStorage.setItem('session_id', id);
        return id;
    }

    // Bell sound URL
    const BELL_SOUND_URL = 'https://res.cloudinary.com/dll4d2yu7/video/upload/v1750831829/nexusgenSoftware/doorbell-110_xxg0im.wav';

    // Voice settings
    const [voiceSettings] = useState({
        rate: 0.7,
        pitch: 1.0,
        volume: 1.0
    });

    // API Functions
    const saveTokenToDatabase = async (token, type = 'called') => {
        try {
            const response = await fetch(`${API_BASE_URL}/token-history`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    token: token,
                    type: type,
                    called_at: new Date().toISOString(),
                    session_id: SESSION_ID,
                    admin_id: ADMIN_ID,
                    metadata: {
                        voice_settings: voiceSettings,
                        browser: navigator.userAgent
                    }
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Token saved to database:', data);
            return data;
        } catch (error) {
            console.error('Error saving token to database:', error);
            toast.error('ڈیٹابیس میں محفوظ کرنے میں خرابی');
            return null;
        }
    };

    const fetchTodaysTokens = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/token-history/todays-tokens`, {
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
                setCalledTokens(data.data.called_tokens || []);
                console.log('Fetched today\'s tokens:', data.data.called_tokens);
            }
        } catch (error) {
            console.error('Error fetching today\'s tokens:', error);
            loadLocalStorageData();
        }
    };

    const fetchTokenHistory = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/token-history?per_page=50`, {
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
                const formattedHistory = data.data.data.map(item => ({
                    token: item.token,
                    time: new Date(item.called_at).toLocaleTimeString(),
                    date: new Date(item.called_at).toLocaleDateString(),
                    type: item.type
                }));
                setTokenHistory(formattedHistory);
                console.log('Fetched token history:', formattedHistory);
            }
        } catch (error) {
            console.error('Error fetching token history:', error);
            loadLocalStorageData();
        }
    };

    const clearTodaysTokensFromDatabase = async () => {
        try {
            setIsClearing(true);
            const response = await fetch(`${API_BASE_URL}/token-history/clear-todays`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
                console.log('Database tokens cleared:', data.message);
                return data.deleted_count;
            }
            return 0;
        } catch (error) {
            console.error('Error clearing tokens from database:', error);
            throw error;
        } finally {
            setIsClearing(false);
        }
    };

    const loadLocalStorageData = () => {
        const savedCurrentToken = localStorage.getItem('currentTokenNumber');
        const savedCalledTokens = localStorage.getItem('calledTokens');
        const savedTokenHistory = localStorage.getItem('tokenHistory');

        if (savedCurrentToken) {
            setCurrentToken(savedCurrentToken);
            setDisplayToken(savedCurrentToken);
        }

        if (savedCalledTokens) {
            try {
                setCalledTokens(JSON.parse(savedCalledTokens));
            } catch (e) {
                console.error('Error loading called tokens:', e);
            }
        }

        if (savedTokenHistory) {
            try {
                setTokenHistory(JSON.parse(savedTokenHistory));
            } catch (e) {
                console.error('Error loading token history:', e);
            }
        }
    };

    // Initialize bell audio on component mount
    useEffect(() => {
        const audio = new Audio(BELL_SOUND_URL);
        audio.preload = 'auto';
        audio.volume = 0.8;
        setBellAudio(audio);
        audio.load();

        return () => {
            if (audio) {
                audio.pause();
                audio.currentTime = 0;
            }
        };
    }, []);

    // Load voices when available
    useEffect(() => {
        const loadVoices = () => {
            const voices = speechSynthesis.getVoices();
            if (voices.length > 0) {
                setVoicesLoaded(true);
                console.log('Available voices:', voices.map(v => `${v.name} (${v.lang})`));
            }
        };

        loadVoices();

        if (speechSynthesis.onvoiceschanged !== undefined) {
            speechSynthesis.onvoiceschanged = loadVoices;
        }

        setTimeout(loadVoices, 1000);
    }, []);

    // Load data from API and localStorage on component mount
    useEffect(() => {
        fetchTodaysTokens();
        fetchTokenHistory();

        const savedCurrentToken = localStorage.getItem('currentTokenNumber');
        if (savedCurrentToken) {
            setCurrentToken(savedCurrentToken);
            setDisplayToken(savedCurrentToken);
        }
    }, []);

    // Save current token to localStorage
    useEffect(() => {
        if (currentToken) {
            localStorage.setItem('currentTokenNumber', currentToken);
        }
    }, [currentToken]);

    // Backup to localStorage
    useEffect(() => {
        localStorage.setItem('calledTokens', JSON.stringify(calledTokens));
    }, [calledTokens]);

    useEffect(() => {
        localStorage.setItem('tokenHistory', JSON.stringify(tokenHistory));
    }, [tokenHistory]);

    // Get previous/next token functions
    const getPreviousToken = (token) => {
        const prefix = token.replace(/\d+$/, '');
        const number = parseInt(token.replace(/^[A-Z]+/, '')) || 1;
        const prevNumber = Math.max(1, number - 1);
        return `${prefix}${String(prevNumber).padStart(3, '0')}`;
    };

    const getNextToken = (token) => {
        const prefix = token.replace(/\d+$/, '');
        const number = parseInt(token.replace(/^[A-Z]+/, '')) || 0;
        const nextNumber = number + 1;
        return `${prefix}${String(nextNumber).padStart(3, '0')}`;
    };

    // Urdu number and letter pronunciation
    const getUrduNumber = (num) => {
        const urduNumbers = {
            0: 'صفر', 1: 'ایک', 2: 'دو', 3: 'تین', 4: 'چار',
            5: 'پانچ', 6: 'چھ', 7: 'سات', 8: 'آٹھ', 9: 'نو'
        };

        return num.toString().split('').map(digit => urduNumbers[parseInt(digit)] || digit).join(' ');
    };

    const getUrduLetter = (letter) => {
        const urduLetters = {
            'A': 'اے', 'B': 'بی', 'C': 'سی', 'D': 'ڈی', 'E': 'ای',
            'F': 'ایف', 'G': 'جی', 'H': 'ایچ', 'I': 'آئی', 'J': 'جے',
            'K': 'کے', 'L': 'ایل', 'M': 'ایم', 'N': 'این', 'O': 'او',
            'P': 'پی', 'Q': 'کیو', 'R': 'آر', 'S': 'ایس', 'T': 'ٹی',
            'U': 'یو', 'V': 'وی', 'W': 'ڈبلیو', 'X': 'ایکس', 'Y': 'وائی', 'Z': 'زیڈ'
        };
        return urduLetters[letter.toUpperCase()] || letter;
    };

    // Create announcements
    const createAnnouncements = (token, isRepeat = false) => {
        const prefix = token.replace(/\d+$/, '');
        const number = parseInt(token.replace(/^[A-Z]+/, '')) || 0;

        const prefixUrdu = prefix.split('').map(char => getUrduLetter(char)).join(' ');
        const numberUrdu = getUrduNumber(number);

        const announcements = [];

        if (isRepeat) {
            announcements.push({
                text: `Token number ${prefix} ${number} again. Please come to counter.`,
                lang: 'en-US'
            });
            announcements.push({
                text: `ٹوکن نمبر ${prefixUrdu} ${numberUrdu} دوبارہ۔ کاؤنٹر پر آئیں۔`,
                lang: 'ur-PK'
            });
            announcements.push({
                text: `टोकन नंबर ${prefix} ${number} Dobara counter par tashreef layein`,
                lang: 'hi-IN'
            });
        } else {
            announcements.push({
                text: `ٹوکن نمبر ${prefixUrdu} ${numberUrdu}۔ کاؤنٹر پر آئیں۔`,
                lang: 'ur-PK'
            });
            announcements.push({
                text: `टोकन नंबर ${prefix} ${number} counter par tashreef layein.`,
                lang: 'hi-IN'
            });
        }

        return announcements;
    };

    // Play bell sound
    const playBellSound = () => {
        return new Promise((resolve) => {
            if (shouldStop) {
                resolve();
                return;
            }

            if (bellAudio) {
                bellAudio.currentTime = 0;

                const playPromise = bellAudio.play();

                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            console.log('Bell sound started playing');
                            bellAudio.onended = () => {
                                console.log('Bell sound finished');
                                resolve();
                            };
                        })
                        .catch(error => {
                            console.error('Error playing bell sound:', error);
                            resolve();
                        });
                } else {
                    setTimeout(() => {
                        resolve();
                    }, 1500);
                }
            } else {
                console.log('Bell audio not loaded');
                resolve();
            }
        });
    };

    // Enhanced speech function with stop control
    const speakToken = async (token, isRepeat = false) => {
        if (!('speechSynthesis' in window)) {
            toast.error('آپ کا براؤزر آواز کو سپورٹ نہیں کرتا');
            return;
        }

        try {
            // Reset stop flag
            setShouldStop(false);
            speechSynthesis.cancel();
            setIsPlaying(true);

            console.log('Playing bell sound first...');
            await playBellSound();

            // Check if stop was requested during bell
            if (shouldStop) {
                console.log('Stop requested during bell sound');
                return;
            }

            await new Promise(resolve => setTimeout(resolve, 300));

            // Check if stop was requested during delay
            if (shouldStop) {
                console.log('Stop requested during delay');
                return;
            }

            const announcements = createAnnouncements(token, isRepeat);
            const voices = speechSynthesis.getVoices();
            console.log('Available voices:', voices.length);

            const speakWithVoice = (announcement, selectedVoice = null) => {
                return new Promise((resolve) => {
                    // Check if stop was requested before speaking
                    if (shouldStop) {
                        console.log('Stop requested before speaking');
                        resolve(false);
                        return;
                    }

                    const utterance = new SpeechSynthesisUtterance();
                    utterance.text = announcement.text;
                    utterance.rate = voiceSettings.rate;
                    utterance.pitch = voiceSettings.pitch;
                    utterance.volume = voiceSettings.volume;
                    utterance.lang = announcement.lang;

                    if (selectedVoice) {
                        utterance.voice = selectedVoice;
                    }

                    console.log(`Speaking: "${announcement.text}" with voice: ${selectedVoice ? selectedVoice.name : 'default'}`);

                    utterance.onend = () => {
                        console.log('Speech completed successfully');
                        resolve(true);
                    };

                    utterance.onerror = (e) => {
                        console.error('Speech error:', e);
                        resolve(false);
                    };

                    // Check again before actually speaking
                    if (shouldStop) {
                        console.log('Stop requested just before speechSynthesis.speak');
                        resolve(false);
                        return;
                    }

                    speechSynthesis.speak(utterance);
                });
            };

            let success = false;

            // Try different voice options, but check for stop after each attempt
            const voiceOptions = [
                () => {
                    const urduVoices = voices.filter(v => v.lang.includes('ur'));
                    if (urduVoices.length > 0) {
                        const urduAnnouncement = announcements.find(a => a.lang === 'ur-PK');
                        return urduAnnouncement ? speakWithVoice(urduAnnouncement, urduVoices[0]) : Promise.resolve(false);
                    }
                    return Promise.resolve(false);
                },
                () => {
                    if (shouldStop) return Promise.resolve(false);
                    const hindiVoices = voices.filter(v => v.lang.includes('hi'));
                    if (hindiVoices.length > 0) {
                        const hindiAnnouncement = announcements.find(a => a.lang === 'hi-IN');
                        return hindiAnnouncement ? speakWithVoice(hindiAnnouncement, hindiVoices[0]) : Promise.resolve(false);
                    }
                    return Promise.resolve(false);
                },
                () => {
                    if (shouldStop) return Promise.resolve(false);
                    const englishVoices = voices.filter(v => v.lang.includes('en'));
                    const femaleVoices = englishVoices.filter(v =>
                        v.name.toLowerCase().includes('female') ||
                        v.name.toLowerCase().includes('zira') ||
                        v.name.toLowerCase().includes('hazel')
                    );
                    const voiceToUse = femaleVoices.length > 0 ? femaleVoices[0] : englishVoices[0];
                    if (voiceToUse) {
                        const englishAnnouncement = announcements.find(a => a.lang === 'en-US');
                        return englishAnnouncement ? speakWithVoice(englishAnnouncement, voiceToUse) : Promise.resolve(false);
                    }
                    return Promise.resolve(false);
                },
                () => {
                    if (shouldStop) return Promise.resolve(false);
                    if (voices.length > 0) {
                        const englishAnnouncement = announcements.find(a => a.lang === 'en-US');
                        return englishAnnouncement ? speakWithVoice(englishAnnouncement, voices[0]) : Promise.resolve(false);
                    }
                    return Promise.resolve(false);
                },
                () => {
                    if (shouldStop) return Promise.resolve(false);
                    const englishAnnouncement = announcements.find(a => a.lang === 'en-US');
                    return englishAnnouncement ? speakWithVoice(englishAnnouncement) : Promise.resolve(false);
                }
            ];

            // Try each voice option until one succeeds or stop is requested
            for (const voiceOption of voiceOptions) {
                if (shouldStop) {
                    console.log('Stop requested, breaking voice option loop');
                    break;
                }
                
                success = await voiceOption();
                if (success || shouldStop) {
                    break;
                }
            }

            if (!success && !shouldStop) {
                toast.error('آواز چلانے میں ناکامی - کوئی آواز دستیاب نہیں');
            }

        } catch (error) {
            console.error('Error in speakToken:', error);
            toast.error('آواز بنانے میں خرابی');
        } finally {
            setIsPlaying(false);
            setShouldStop(false);
        }
    };

    // Test voice function
    const testVoice = async () => {
        const testToken = displayToken || currentToken;
        toast.info('آواز کی جانچ شروع ہو رہی ہے... (پہلے گھنٹی پھر ٹوکن)');

        if (speechSynthesis.paused) {
            speechSynthesis.resume();
        }

        await speakToken(testToken);
    };

    // Call current token
    const callCurrentToken = async () => {
        const tokenToCall = displayToken || currentToken;

        if (!calledTokens.includes(tokenToCall)) {
            setCalledTokens(prev => [...prev, tokenToCall]);
        }

        const historyEntry = {
            token: tokenToCall,
            time: new Date().toLocaleTimeString(),
            date: new Date().toLocaleDateString(),
            type: 'called'
        };

        setTokenHistory(prev => [historyEntry, ...prev.slice(0, 49)]);

        // Save to database
        await saveTokenToDatabase(tokenToCall, 'called');

        if (speechSynthesis.paused) {
            speechSynthesis.resume();
        }

        await speakToken(tokenToCall);
        toast.success(`ٹوکن ${tokenToCall} کال کیا گیا (گھنٹی + اعلان)`);
    };

    // Repeat last token
    const repeatLastToken = async () => {
        if (tokenHistory.length > 0) {
            const lastToken = tokenHistory[0].token;

            if (speechSynthesis.paused) {
                speechSynthesis.resume();
            }

            await speakToken(lastToken, true);

            const historyEntry = {
                token: lastToken,
                time: new Date().toLocaleTimeString(),
                date: new Date().toLocaleDateString(),
                type: 'repeat'
            };

            setTokenHistory(prev => [historyEntry, ...prev.slice(0, 49)]);

            // Save to database
            await saveTokenToDatabase(lastToken, 'repeat');

            toast.info(`ٹوکن ${lastToken} دوبارہ کال کیا گیا (گھنٹی + اعلان)`);
        } else {
            toast.warning('دوبارہ کال کرنے کے لیے کوئی ٹوکن نہیں');
        }
    };

    // Enhanced stop speech and bell function
    const stopSpeech = () => {
        console.log('Stop speech requested');
        
        // Set stop flag to prevent further announcements
        setShouldStop(true);
        
        // Cancel any ongoing speech
        if ('speechSynthesis' in window) {
            speechSynthesis.cancel();
        }
        
        // Stop bell audio
        if (bellAudio) {
            bellAudio.pause();
            bellAudio.currentTime = 0;
        }
        
        // Reset playing state
        setIsPlaying(false);
        
        toast.info('آواز اور گھنٹی بند کر دی گئی');
    };

    // Navigation functions
    const moveToNextToken = () => {
        const nextToken = getNextToken(displayToken || currentToken);
        setDisplayToken(nextToken);
        setCurrentToken(nextToken);
        localStorage.setItem('currentTokenNumber', nextToken);
        toast.info(`اگلا ٹوکن ${nextToken}`);
    };

    const moveToPreviousToken = () => {
        const prevToken = getPreviousToken(displayToken || currentToken);
        setDisplayToken(prevToken);
        toast.info(`پچھلا ٹوکن ${prevToken}`);
    };

    // Manual token input
    const handleManualTokenInput = () => {
        const newToken = prompt("ٹوکن نمبر داخل کریں (جیسے A001, B015):", displayToken);
        if (newToken && newToken.trim()) {
            const formattedToken = newToken.trim().toUpperCase();
            setDisplayToken(formattedToken);
            toast.success(`ٹوکن سیٹ کیا گیا ${formattedToken}`);
        }
    };

    // Clear functions
    const clearCalledTokens = async () => {
        if (window.confirm('کیا آپ واقعی تمام کال شدہ ٹوکن صاف کرنا چاہتے ہیں؟')) {
            try {
                const deletedCount = await clearTodaysTokensFromDatabase();
                setCalledTokens([]);
                localStorage.removeItem('calledTokens');
                toast.success(`${deletedCount} کال شدہ ٹوکن صاف کر دیے گئے`);
            } catch (error) {
                console.error('Error clearing called tokens:', error);
                setCalledTokens([]);
                localStorage.removeItem('calledTokens');
                toast.warning('لوکل ڈیٹا صاف کر دیا گیا، لیکن ڈیٹابیس میں خرابی');
            }
        }
    };

    const clearTokenHistory = async () => {
        if (window.confirm('کیا آپ واقعی ٹوکن کی تاریخ صاف کرنا چاہتے ہیں؟')) {
            try {
                // Note: Uncomment the clearTokenHistoryFromDatabase function if you have it
                // const deletedCount = await clearTokenHistoryFromDatabase();
                setTokenHistory([]);
                localStorage.removeItem('tokenHistory');
                toast.success('تاریخ کے ریکارڈ صاف کر دیے گئے');
            } catch (error) {
                console.error('Error clearing token history:', error);
                setTokenHistory([]);
                localStorage.removeItem('tokenHistory');
                toast.warning('لوکل ڈیٹا صاف کر دیا گیا، لیکن ڈیٹابیس میں خرابی');
            }
        }
    };

    // Voice status indicator
    const getVoiceStatus = () => {
        if (!('speechSynthesis' in window)) {
            return { status: 'error', message: 'آپ کا براؤزر آواز کو سپورٹ نہیں کرتا' };
        }

        const voices = speechSynthesis.getVoices();
        if (voices.length === 0) {
            return { status: 'warning', message: 'آوازیں لوڈ ہو رہی ہیں...' };
        }

        const bellStatus = bellAudio ? 'گھنٹی تیار' : 'گھنٹی لوڈ ہو رہی';
        return { status: 'success', message: `${voices.length} آوازیں دستیاب ہیں | ${bellStatus}` };
    };

    const voiceStatus = getVoiceStatus();

    return (
        <>
            <div className="content">
                <Sidebar />
                <div className="container-fluid pt-4 px-4">
                    <div className="bg-light text-center rounded p-4">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h6 className="mb-0"> ٹوکن کالنگ سسٹم</h6>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => window.location.reload()}
                            >
                                <i className="fa fa-refresh"></i> ریفریش
                            </button>
                        </div>

                        {/* Bell Status Indicator */}
                        <div className="alert alert-info mb-3">
                            <i className="fa fa-bell me-2"></i>
                            <strong>گھنٹی کی حالت:</strong>
                            <br />
                            <small>ہر ٹوکن کال سے پہلے گھنٹی بجے گی</small>
                        </div>

                        {/* Main Token Display */}
                        <div className="token-display-section mb-4">
                            <div className="current-token-display">
                                <h1 className="display-1 token-number">{displayToken || currentToken}</h1>
                                <p className="token-status">
                                    {calledTokens.includes(displayToken || currentToken) ?
                                        <span className="badge bg-success">پہلے سے کال شدہ</span> :
                                        <span className="badge bg-warning">ابھی کال نہیں ہوا</span>
                                    }
                                </p>
                            </div>
                        </div>

                        {/* Control Buttons */}
                        <div className="control-buttons mb-4">
                            <div className="row g-2">
                                <div className="col-md-3">
                                    <button
                                        className="btn btn-call w-100"
                                        onClick={callCurrentToken}
                                        disabled={isPlaying}
                                        style={{ backgroundColor: '#28a745', color: 'white', border: 'none' }}
                                    >
                                        <i className="fa fa-bell me-2"></i>
                                        {isPlaying ? 'کال ہو رہا ہے...' : 'ٹوکن کال'}
                                    </button>
                                </div>
                                <div className="col-md-3">
                                    <button
                                        className="btn btn-repeat w-100"
                                        onClick={repeatLastToken}
                                        disabled={isPlaying || tokenHistory.length === 0}
                                        style={{ backgroundColor: '#ffc107', color: 'black', border: 'none' }}
                                    >
                                        <i className="fa fa-redo me-2"></i>
                                        دوبارہ کال
                                    </button>
                                </div>
                                <div className="col-md-3">
                                    <button
                                        className="btn btn-stop w-100"
                                        onClick={stopSpeech}
                                        disabled={!isPlaying}
                                        style={{ backgroundColor: '#dc3545', color: 'white', border: 'none' }}
                                    >
                                        <i className="fa fa-stop me-2"></i>
                                        بند کریں
                                    </button>
                                </div>
                                <div className="col-md-3">
                                    <button
                                        className="btn btn-manual w-100"
                                        onClick={handleManualTokenInput}
                                        disabled={isPlaying}
                                        style={{ backgroundColor: '#17a2b8', color: 'white', border: 'none' }}
                                    >
                                        <i className="fa fa-edit me-2"></i>
                                        دستی انٹری
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Navigation Buttons */}
                        <div className="navigation-buttons mb-4">
                            <div className="row g-2">
                                <div className="col-md-6">
                                    <button
                                        className="btn btn-outline-primary w-100"
                                        onClick={moveToPreviousToken}
                                    >
                                        <i className="fa fa-arrow-left me-2"></i>
                                        پچھلا ({getPreviousToken(displayToken || currentToken)})
                                    </button>
                                </div>
                                <div className="col-md-6">
                                    <button
                                        className="btn btn-outline-primary w-100"
                                        onClick={moveToNextToken}
                                    >
                                        اگلا ({getNextToken(displayToken || currentToken)})
                                        <i className="fa fa-arrow-right ms-2"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Statistics */}
                        <div className="row mb-4">
                            <div className="col-md-6">
                                <div className="stats-card" style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '15px' }}>
                                    <h6>آج کے کال شدہ ٹوکن</h6>
                                    <div className="called-tokens-list">
                                        {calledTokens.length > 0 ? (
                                            <div className="tokens-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                                {calledTokens.slice(-10).map((token, index) => (
                                                    <span key={index} className="token-badge called" style={{
                                                        backgroundColor: '#28a745',
                                                        color: 'white',
                                                        padding: '5px 10px',
                                                        borderRadius: '15px',
                                                        fontSize: '12px'
                                                    }}>
                                                        {token}
                                                    </span>
                                                ))}
                                                {calledTokens.length > 10 && (
                                                    <span className="token-badge more" style={{
                                                        backgroundColor: '#6c757d',
                                                        color: 'white',
                                                        padding: '5px 10px',
                                                        borderRadius: '15px',
                                                        fontSize: '12px'
                                                    }}>
                                                        +{calledTokens.length - 10} مزید
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-muted">ابھی کوئی ٹوکن کال نہیں ہوا</p>
                                        )}
                                        {calledTokens.length > 0 && (
                                            <button
                                                className="btn btn-outline-danger btn-sm mt-2"
                                                onClick={clearCalledTokens}
                                                disabled={isClearing}
                                            >
                                                {isClearing ? 'صاف ہو رہا...' : 'تمام صاف کریں'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="stats-card">
                                    <h6>حالیہ تاریخ</h6>
                                    <div className="history-list">
                                        {tokenHistory.length > 0 ? (
                                            <div className="history-items">
                                                {tokenHistory.slice(0, 5).map((entry, index) => (
                                                    <div key={index} className="history-item">
                                                        <span className={`token-badge ${entry.type}`}>
                                                            {entry.token}
                                                        </span>
                                                        <small className="time-stamp">
                                                            {entry.time} ({entry.type === 'repeat' ? 'دوبارہ' : 'کال'})
                                                        </small>
                                                    </div>
                                                ))}
                                                <button
                                                    className="btn btn-outline-danger btn-sm mt-2"
                                                    onClick={clearTokenHistory}
                                                    disabled={isClearing}
                                                >
                                                    {isClearing ? 'صاف ہو رہا...' : 'تاریخ صاف کریں'}
                                                </button>
                                            </div>
                                        ) : (
                                            <p className="text-muted">کوئی تاریخ دستیاب نہیں</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Instructions */}
                        {/* <div className="instructions-card">
                            <h6><i className="fa fa-info-circle me-2"></i>بہتر آواز سسٹم کی خصوصیات</h6>
                            <div className="row text-start">
                                <div className="col-md-6">
                                    <ul className="instruction-list">
                                        <li><strong>گھنٹی کی آواز:</strong> ہر ٹوکن کال سے پہلے گھنٹی بجتی ہے</li>
                                        <li><strong>متعدد زبانیں:</strong> اردو، ہندی، اور انگریزی میں اعلان</li>
                                        <li><strong>خودکار آواز:</strong> سب سے بہترین آواز خودکار منتخب ہوتی ہے</li>
                                        <li><strong>بہتر تلفظ:</strong> صاف اور سمجھنے والا اعلان</li>
                                    </ul>
                                </div>
                                <div className="col-md-6">
                                    <ul className="instruction-list">
                                        <li><strong>اردو نمبر:</strong> 0=صفر، 1=ایک، 2=دو، 3=تین</li>
                                        <li><strong>اردو حروف:</strong> A=اے، B=بی، C=سی</li>
                                        <li><strong>مریضوں کے لیے:</strong> "کاؤنٹر پر آئیں"</li>
                                        <li><strong>محفوظ ڈیٹا:</strong> تمام معلومات خودکار محفوظ</li>
                                    </ul>
                                </div>
                            </div>
                            <div className="alert alert-info mt-3">
                                <strong>اہم:</strong> اگر آواز نہیں آ رہی تو پہلے "آواز ٹیسٹ کریں" بٹن دبائیں۔ 
                                یہ براؤزر کو آواز چلانے کی اجازت دے دیتا ہے۔
                            </div>
                        </div> */}
                    </div>
                </div>
                <Footer />
            </div>

            <style jsx>{`
                .token-display-section {
                    background: linear-gradient(135deg, #2e7d32 0%, #388e3c 100%);
                    border-radius: 20px;
                    padding: 40px 20px;
                    color: white;
                    box-shadow: 0 10px 30px rgba(46, 125, 50, 0.3);
                }

                .current-token-display {
                    text-align: center;
                }

                .token-number {
                    font-size: 5rem;
                    font-weight: bold;
                    text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
                    margin: 0;
                    letter-spacing: 0.2em;
                }

                .token-status {
                    font-size: 1.2rem;
                    margin-top: 10px;
                }

                .control-buttons .btn {
                    padding: 15px 20px;
                    font-size: 16px;
                    font-weight: 600;
                    border-radius: 10px;
                    transition: all 0.3s ease;
                    margin-bottom: 10px;
                }

                .btn-call {
                    background-color: #2e7d32;
                    border-color: #2e7d32;
                    color: white;
                }

                .btn-call:hover:not(:disabled) {
                    background-color: #1b5e20;
                    border-color: #1b5e20;
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(46, 125, 50, 0.4);
                }

                .btn-repeat {
                    background-color: #ffc107;
                    border-color: #ffc107;
                    color: #212529;
                }

                .btn-repeat:hover:not(:disabled) {
                    background-color: #e0a800;
                    border-color: #e0a800;
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(255, 193, 7, 0.4);
                }

                .btn-stop {
                    background-color: #dc3545;
                    border-color: #dc3545;
                    color: white;
                }

                .btn-stop:hover:not(:disabled) {
                    background-color: #c82333;
                    border-color: #c82333;
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(220, 53, 69, 0.4);
                }

                .btn-manual {
                    background-color: #6f42c1;
                    border-color: #6f42c1;
                    color: white;
                }

                .btn-manual:hover:not(:disabled) {
                    background-color: #5a2d91;
                    border-color: #5a2d91;
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(111, 66, 193, 0.4);
                }

                .voice-test {
                    background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
                    padding: 20px;
                    border-radius: 15px;
                    color: white;
                    text-align: center;
                }

                .voice-test .btn {
                    background-color: white;
                    color: #17a2b8;
                    border: none;
                    font-weight: bold;
                    padding: 15px 30px;
                }

                .voice-test .btn:hover:not(:disabled) {
                    background-color: #f8f9fa;
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(0,0,0,0.2);
                }

                .navigation-buttons .btn {
                    padding: 12px 20px;
                    font-size: 14px;
                    border-radius: 8px;
                }

                .stats-card {
                    background-color: #fff;
                    padding: 20px;
                    border-radius: 10px;
                    border: 1px solid #dee2e6;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
                    height: 100%;
                }

                .stats-card h6 {
                    color: #495057;
                    margin-bottom: 15px;
                    font-weight: 600;
                }

                .tokens-grid {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    margin-bottom: 10px;
                }

                .token-badge {
                    display: inline-block;
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: bold;
                    text-align: center;
                    min-width: 50px;
                }

                .token-badge.called {
                    background-color: #d4edda;
                    color: #155724;
                    border: 1px solid #c3e6cb;
                }

                .token-badge.more {
                    background-color: #e2e3e5;
                    color: #6c757d;
                    border: 1px solid #ced4da;
                }

                .token-badge.repeat {
                    background-color: #fff3cd;
                    color: #856404;
                    border: 1px solid #ffeaa7;
                }

                .history-items {
                    max-height: 200px;
                    overflow-y: auto;
                }

                .history-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 8px 0;
                    border-bottom: 1px solid #f1f1f1;
                }

                .history-item:last-child {
                    border-bottom: none;
                }

                .time-stamp {
                    color: #6c757d;
                    font-size: 11px;
                }

                .instructions-card {
                    background-color: #e8f5e8;
                    padding: 20px;
                    border-radius: 10px;
                    border-left: 4px solid #28a745;
                }

                .instructions-card h6 {
                    color: #155724;
                    margin-bottom: 15px;
                }

                .instruction-list {
                    list-style: none;
                    padding-left: 0;
                }

                .instruction-list li {
                    margin-bottom: 8px;
                    font-size: 14px;
                    color: #495057;
                }

                .instruction-list strong {
                    color: #155724;
                }

                .badge {
                    font-size: 0.75em;
                    padding: 0.5em 0.75em;
                }

                /* Animation for playing state */
                .btn-call:disabled {
                    animation: pulse 2s infinite;
                }

                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                    100% { transform: scale(1); }
                }

                /* Responsive Design */
                @media (max-width: 768px) {
                    .token-number {
                        font-size: 3rem;
                    }
                    
                    .control-buttons .btn {
                        padding: 12px 15px;
                        font-size: 14px;
                        margin-bottom: 8px;
                    }
                }

                .bg-success {
                    background-color: #28a745 !important;
                }

                .bg-warning {
                    background-color: #ffc107 !important;
                    color: #212529 !important;
                }
            `}</style>
        </>
    );
}